import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useFormatters } from '../../contexts/PreferencesContext'
import { useAuth } from '../../contexts/AuthContext'
import { usePortalLabels } from '../../lib/portalLabels'
import { ChartWrapper } from './ChartWrapper'
import { PPVChartPanels } from './PPVCharts'
import type { DrilldownFilters } from '../drilldown'
import { DashboardData, PPVViewData } from './types'

interface CostHistoricalViewProps {
  data: DashboardData
  /** W3-T14: the Historical Arrears Trend plots frozen snapshots, so it is Historical-mode only. */
  dataMode?: 'historical' | 'live'
  /** CR-5456: server-aggregated Historical PPV (received-qty grain); absent for suppliers. */
  ppvView?: PPVViewData
  /** CR-5456: scope-only filters for the PPV panels (no dashboard date window). */
  ppvDrilldownFilters: DrilldownFilters
  onChartClick: (chartData: unknown, metricType: 'spend' | 'arrears' | 'ppv' | 'open_order_value') => void
}

/**
 * Historical sub-view of the Cost dashboard tab.
 *
 * CR-5456: extracted from CostTab.tsx (which was over the 500-line limit) and
 * extended with the Historical PPV panels — realised variance on received
 * goods, plotted against the original required date.
 */
export function CostHistoricalView({
  data,
  dataMode,
  ppvView,
  ppvDrilldownFilters,
  onChartClick,
}: CostHistoricalViewProps) {
  const { formatCurrency, formatCompactCurrency, preferences } = useFormatters()
  // SI-23: the subtitle must follow the portal the viewer is in — a supplier
  // sees "Goods delivered", not "Goods received" — and name the DISPLAY
  // currency, which is not the base currency on a mixed-currency roll-up.
  const { user } = useAuth()
  const portalLabels = usePortalLabels(user?.platform)

  return (
    <div className="space-y-6">
      {/* Historical Cost Overview */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-gray-600 text-white p-6 rounded-lg text-center">
          <h3 className="text-lg font-medium mb-2">Historical Spend</h3>
          <div className="text-3xl font-bold">{formatCurrency(data.cost?.totalSpend || 0)}</div>
          <p className="text-sm mt-2 opacity-80">Last 12 months</p>
        </div>

        <div className="bg-gray-600 text-white p-6 rounded-lg text-center">
          <h3 className="text-lg font-medium mb-2">Total Arrears (Historical)</h3>
          <div className="text-3xl font-bold">{formatCurrency(data.cost?.historicalArrears || 0)}</div>
          <p className="text-sm mt-2 opacity-80">Cumulative</p>
        </div>

        <div className="bg-gray-600 text-white p-6 rounded-lg text-center">
          <h3 className="text-lg font-medium mb-2">Cost Reduction</h3>
          <div className={`text-3xl font-bold ${(data.deltas?.total_spend?.percentage_change ?? 0) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {data.deltas?.total_spend?.percentage_change != null ? `${data.deltas.total_spend.percentage_change >= 0 ? '+' : ''}${data.deltas.total_spend.percentage_change.toFixed(1)}%` : 'N/A'}
          </div>
          <p className="text-sm mt-2 opacity-80">Month over month</p>
        </div>
      </div>

      {/* Historical Charts */}
      <div className="grid grid-cols-2 gap-6">
        <ChartWrapper
          title="Historical Spend Trend"
          subtitle={`${portalLabels.spendByMonthSubtitle} ${preferences.currency}`}
          data={data.charts?.spendByMonth || []}
          exportFilename="historical-spend-trend"
        >
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={data.charts?.spendByMonth || []}
              onClick={(chartData) => onChartClick(chartData, 'spend')}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompactCurrency(value)} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
              <Line type="monotone" dataKey="spend" stroke="#3b82f6" strokeWidth={2} name="Spend" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper
          title="Historical Arrears Trend"
          data={dataMode === 'live' ? undefined : (data.charts?.historicalArrears || [])}
          exportFilename="historical-arrears-trend"
        >
          {/* W3-T14 (FIX-04): the frozen month-end snapshot value per month; a
              month never captured is a gap, the current month is today's stock. */}
          {dataMode === 'live' ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              Month-end snapshots are shown in Historical mode. Switch Data to Historical to see the trend.
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-2">Frozen month-end arrears value; a month with no snapshot is blank</p>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart
                  data={data.charts?.historicalArrears || []}
                  onClick={(chartData) => onChartClick(chartData, 'arrears')}
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompactCurrency(value)} />
                  <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
                  <Line type="monotone" dataKey="value" stroke="#ef4444" strokeWidth={2} name="Arrears" dot={{ r: 3 }} connectNulls={false} />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </ChartWrapper>
      </div>

      {/* CR-5456: Historical PPV — realised variance on received goods (GRN
          quantity), plotted against the original required date. Absent for
          supplier users (backend gates the data out server-side). */}
      {ppvView && (
        <PPVChartPanels view={ppvView} isFuture={false} drilldownFilters={ppvDrilldownFilters} />
      )}
    </div>
  )
}
